export interface CompressionResult {
  webpDataUrl: string;
  sizeKb: number;
  originalSizeKb: number;
  reductionPercentage: number;
}

/**
 * Convert any uploaded raw File (PNG, JPG, JPEG, 1MB-15MB)
 * to an optimized crisp WebP Data URL targeting 100KB - 200KB MAX without loss of quality.
 */
export async function convertFileToWebP(
  file: File,
  maxDimension = 1600,
  initialQuality = 0.88
): Promise<string> {
  const res = await compressFileToWebPDetailed(file, maxDimension, initialQuality);
  return res.webpDataUrl;
}

/**
 * Detailed converter with iterative quality optimization targeting 100KB - 200KB.
 */
export async function compressFileToWebPDetailed(
  file: File,
  maxDimension = 1600,
  initialQuality = 0.88
): Promise<CompressionResult> {
  const originalSizeKb = Math.round(file.size / 1024);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Resize proportionally if larger than maxDimension (e.g. 1600px)
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          const fallbackStr = e.target?.result as string;
          resolve({
            webpDataUrl: fallbackStr,
            sizeKb: originalSizeKb,
            originalSizeKb,
            reductionPercentage: 0
          });
          return;
        }

        // Enable high-quality bicubic image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        let quality = initialQuality;
        let webpDataUrl = canvas.toDataURL('image/webp', quality);
        let sizeKb = Math.round((webpDataUrl.length * 0.75) / 1024);

        // Target range: 100KB to 200KB max
        // If file is > 200KB, gradually reduce quality
        while (sizeKb > 200 && quality > 0.45) {
          quality -= 0.08;
          webpDataUrl = canvas.toDataURL('image/webp', quality);
          sizeKb = Math.round((webpDataUrl.length * 0.75) / 1024);
        }

        const reductionPercentage =
          originalSizeKb > 0
            ? Math.max(0, Math.round(((originalSizeKb - sizeKb) / originalSizeKb) * 100))
            : 0;

        resolve({
          webpDataUrl,
          sizeKb,
          originalSizeKb,
          reductionPercentage
        });
      };
      img.onerror = () => {
        const fallbackStr = e.target?.result as string;
        resolve({
          webpDataUrl: fallbackStr,
          sizeKb: originalSizeKb,
          originalSizeKb,
          reductionPercentage: 0
        });
      };
      img.src = e.target?.result as string;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

/**
 * Batch convert up to 10 files into optimized WebP formats (100KB - 200KB).
 */
export async function convertMultipleFilesToWebP(
  files: FileList | File[],
  maxCount = 10
): Promise<CompressionResult[]> {
  const fileArray = Array.from(files).slice(0, maxCount);
  const results: CompressionResult[] = [];

  for (const file of fileArray) {
    if (file.type.startsWith('image/')) {
      const res = await compressFileToWebPDetailed(file);
      results.push(res);
    }
  }

  return results;
}

/**
 * Validate & Read Brochure upload: Supports up to 25 MB documents seamlessly.
 * Auto-handles PDF files, computes size, and converts to reliable Data URL / Storage URL.
 */
export async function validateAndReadBrochure(
  file: File,
  maxMb = 25
): Promise<{ valid: boolean; error?: string; dataUrl?: string; sizeMb?: number }> {
  const sizeMb = file.size / (1024 * 1024);

  if (sizeMb > maxMb) {
    return {
      valid: false,
      sizeMb: Number(sizeMb.toFixed(2)),
      error: `Brochure file size exceeds ${maxMb} MB limit (Selected: ${sizeMb.toFixed(2)} MB). Please share a smaller file or external link.`
    };
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve({
        valid: true,
        sizeMb: Number(sizeMb.toFixed(2)),
        dataUrl: e.target?.result as string
      });
    };
    reader.onerror = () => {
      resolve({
        valid: false,
        sizeMb: Number(sizeMb.toFixed(2)),
        error: 'Failed to read brochure document.'
      });
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Converts a remote image URL to an optimized permanent WebP Data URL (100-200 KB).
 * If CORS prevents canvas extraction, falls back gracefully to the original URL.
 */
export async function convertImageUrlToWebP(
  imageUrl: string,
  maxDimension = 1600,
  initialQuality = 0.88
): Promise<{ webpDataUrl: string; sizeKb?: number; converted: boolean }> {
  if (imageUrl.startsWith('data:image/webp')) {
    const sizeKb = Math.round((imageUrl.length * 0.75) / 1024);
    return { webpDataUrl: imageUrl, sizeKb, converted: true };
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        let width = img.width;
        let height = img.height;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve({ webpDataUrl: imageUrl, converted: false });
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        let quality = initialQuality;
        let webpDataUrl = canvas.toDataURL('image/webp', quality);
        let sizeKb = Math.round((webpDataUrl.length * 0.75) / 1024);

        while (sizeKb > 200 && quality > 0.45) {
          quality -= 0.08;
          webpDataUrl = canvas.toDataURL('image/webp', quality);
          sizeKb = Math.round((webpDataUrl.length * 0.75) / 1024);
        }

        resolve({ webpDataUrl, sizeKb, converted: true });
      } catch {
        // Tainted canvas (CORS restricted by remote host) - fallback to URL
        resolve({ webpDataUrl: imageUrl, converted: false });
      }
    };

    img.onerror = () => {
      resolve({ webpDataUrl: imageUrl, converted: false });
    };

    img.src = imageUrl;
  });
}
